import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Pagination, Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { EditOutlined } from '@ant-design/icons';
import { useLazyGetAllAbsenceQuery } from 'app/services/edpApiV1/absence';
import { useSubordinatesQuery } from 'app/services/edpApiV1/viewer';
import { dataForAbsencesTable, sortingDate } from 'shared/helpers/utils';
import { usePullSort } from 'shared/hooks';
import { RolesUser, SortCondition } from 'shared/enums';
import { useIndicators } from 'features/indicators';
import { TPreparedAbsenceData } from 'shared/types/absence.types';
import SearchComponent from 'shared/components/search/news-faq';
import { SearchKeysNews } from 'shared/enums/news';
import StringFormItem from 'shared/components/search/news-faq/stringFormItem/StringFormItem';
import NumberFormItem from 'shared/components/search/news-faq/numberFormItem/NumberFormItem';
import { RequestsSearch } from 'shared/types/types';
import { getAllAvailableOrganizations } from 'entities/organizations/model/organizations';
import { useAppSelector } from 'app/store';
import { viewerModel } from 'entities/viewer';
import ModalAbsenceSubordinates from '../modalAbsenceSubordinates';
import style from './index.module.scss';

const AbsenceSubordinates = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [currentSize, setCurrentSize] = useState<number>(10);
  const [dataHrSubordinates, setDataHrSubordinates] = useState<TPreparedAbsenceData[] | []>([]);
  const [openModal, setOpenModal] = useState(false);
  const [requestId, setRequestId] = useState<number>();
  const [hrIdCurrent, setHrIdCurrent] = useState<number>();
  const [sortInfo, trigger] = usePullSort();
  const [search, setSearch] = useState<RequestsSearch>({ byId: undefined, fio: undefined });
  const allAvailableOrganizations = useAppSelector(getAllAvailableOrganizations);
  const { role } = useAppSelector(viewerModel.me.selectors.selectViewerProfile);

  const handleChangeResponsible = (id: number, hrId: number): void => {
    setRequestId(id);
    setOpenModal(true);
    setHrIdCurrent(hrId);
  };

  const handlePagination = (value: number, pageSize: number) => {
    setCurrentSize(pageSize);
    setCurrentPage(value);
  };

  const onCell = (record: TPreparedAbsenceData) => ({
    className: style.pointer,
    onClick: () => {
      navigate(`/absence/user/subordination/${record.id}`);
    },
  });

  const columns: ColumnsType<TPreparedAbsenceData> = [
    {
      title: '№',
      dataIndex: 'id',
      key: 'key',
      width: 120,
      onCell,
    },
    {
      title: 'Дата',
      dataIndex: 'created',
      key: 'created',
      sortDirections: [SortCondition.ASCEND, SortCondition.DESCEND],
      sorter: (a, b) => sortingDate(a.created, b.created),
      onHeaderCell: () => ({
        onClick: () => trigger(),
      }),
      onCell,
    },
    {
      title: 'Период',
      dataIndex: 'period',
      key: 'period',
      onCell,
    },
    {
      title: 'ФИО',
      dataIndex: 'fio',
      key: 'fio',
      onCell,
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: status => (
        <span>
          <Badge status={status.type} />
          {status.name}
        </span>
      ),
      onCell,
    },
  ];

  if (!allAvailableOrganizations && role === RolesUser.HRDirector) {
    columns.push({
      title: 'Ответственный',
      dataIndex: 'hr',
      key: 'hr',
      render: (hr, record: { key: React.Key }) => {
        return (
          <>
            {hr?.name || hr?.middleName || hr?.lastName ? (
              <>
                <span>{`${hr.lastName} ${hr.name} ${hr.middleName ?? ''}`}</span>
                <EditOutlined
                  className={style.btnChangeResponsible}
                  onClick={() => handleChangeResponsible(Number(record.key), hr.id)}
                />
              </>
            ) : (
              <span
                className={style.changeResponsible}
                onClick={() => handleChangeResponsible(Number(record.key), hr?.id)}
              >
                Назначить
              </span>
            )}
          </>
        );
      },
    });
  }

  const [getAllAbsenceTrigger, resultAllAbsence] = useLazyGetAllAbsenceQuery();

  const onSearch = (data: string, key: SearchKeysNews) => {
    if (key === SearchKeysNews.SEARCH_ID) {
      setSearch({ fio: undefined, byId: data ? data : undefined });
    } else if (key === SearchKeysNews.SEARCH_NAME) {
      setSearch({ byId: undefined, fio: data ? data : undefined });
    } else {
      setSearch({ [key]: data ? data : undefined });
    }
  };

  useEffect(() => {
    if (sortInfo.value === null)
      getAllAbsenceTrigger({
        type: 'subordinatesInWork',
        page: currentPage,
        size: currentSize,
        allAvailableOrganizations,
        ...search,
      });
    if (sortInfo.value === SortCondition.ASCEND)
      getAllAbsenceTrigger({
        type: 'subordinatesInWork',
        page: currentPage,
        size: currentSize,
        order: SortCondition.ASC,
        allAvailableOrganizations,
        ...search,
      });
    if (sortInfo.value === SortCondition.DESCEND)
      getAllAbsenceTrigger({
        type: 'subordinatesInWork',
        page: currentPage,
        size: currentSize,
        order: SortCondition.DESC,
        allAvailableOrganizations,
        ...search,
      });
  }, [getAllAbsenceTrigger, currentPage, sortInfo.value, currentSize, search, allAvailableOrganizations]);

  const { data: allSubordinates } = useSubordinatesQuery({ include: 'me' });

  useEffect(() => {
    if (resultAllAbsence && resultAllAbsence?.data?.data) {
      const dataTable = dataForAbsencesTable(resultAllAbsence.data.data);
      if (dataTable) {
        setDataHrSubordinates(dataTable);
      }
    }
  }, [resultAllAbsence]);

  // подписываемся на обновление индикаторов, после обновления списка неявок
  useIndicators(resultAllAbsence.isSuccess, resultAllAbsence.isFetching);

  return (
    <>
      <SearchComponent
        selectOptions={{
          [SearchKeysNews.SEARCH_NAME]: {
            name: SearchKeysNews.SEARCH_NAME,
            alias: 'ФИО',
            node: StringFormItem({
              name: SearchKeysNews.SEARCH_NAME,
              placeholder: 'Введите ФИО',
              onSearch: data => {
                onSearch?.(data, SearchKeysNews.SEARCH_NAME);
              },
            }),
          },
          [SearchKeysNews.SEARCH_ID]: {
            name: SearchKeysNews.SEARCH_ID,
            alias: 'Номер заявки',
            node: NumberFormItem({
              name: SearchKeysNews.SEARCH_ID,
              placeholder: 'Введите номер заявки',
              onSearch: data => {
                onSearch?.(data, SearchKeysNews.SEARCH_ID);
              },
            }),
          },
          [SearchKeysNews.PERSONNEL_NUMBER]: {
            name: SearchKeysNews.PERSONNEL_NUMBER,
            alias: 'ТН',
            node: StringFormItem({
              name: SearchKeysNews.PERSONNEL_NUMBER,
              placeholder: 'Введите ТН',
              onSearch: data => {
                onSearch?.(data, SearchKeysNews.PERSONNEL_NUMBER);
              },
            }),
          },
          [SearchKeysNews.POSITION_NAME]: {
            name: SearchKeysNews.POSITION_NAME,
            alias: 'Должность',
            node: StringFormItem({
              name: SearchKeysNews.POSITION_NAME,
              placeholder: 'Введите должность',
              onSearch: data => {
                onSearch?.(data, SearchKeysNews.POSITION_NAME);
              },
            }),
          },
          [SearchKeysNews.DIVISION_NAME]: {
            name: SearchKeysNews.DIVISION_NAME,
            alias: 'Подразделение',
            node: StringFormItem({
              name: SearchKeysNews.DIVISION_NAME,
              placeholder: 'Введите подразделение',
              onSearch: data => {
                onSearch?.(data, SearchKeysNews.DIVISION_NAME);
              },
            }),
          },
        }}
      />
      <Table scroll={{ x: true }} dataSource={dataHrSubordinates} columns={columns} pagination={false} bordered />
      <div className={style.paginationWrap}>
        <Pagination
          defaultCurrent={currentSize}
          current={currentPage}
          showSizeChanger
          total={resultAllAbsence?.data?.pagination?.total}
          pageSize={currentSize}
          onChange={(value, pageSize) => {
            handlePagination(value, pageSize);
          }}
        />
      </div>
      {allSubordinates && allSubordinates.length > 0 && requestId && hrIdCurrent && (
        <ModalAbsenceSubordinates
          openModal={openModal}
          setOpenModal={setOpenModal}
          requestId={requestId}
          hrIdCurrent={hrIdCurrent}
        />
      )}
    </>
  );
};

export default AbsenceSubordinates;
